import { http } from "../../../shared/api/http"

/* ===================== TYPES ===================== */

export type ChatParticipant = {
  user_id: string
  full_name: string
  avatar: string
}

export type DirectChatPeer = {
  user_id: string
  full_name: string | null
  phone_number: string | null
  email: string
  display_name: string | null
  avatar: string | null
}

export type LastMessage = {
  type: "text" | "image" | string
  text: string
  attachments_count: number
  sender_id: string
}

export type ChatItem = {
  id: string
  chat_type: "direct" | "course" | string
  peer: DirectChatPeer | null
  course_id: string | null
  course_title?: string | null
  last_message: LastMessage | null
  last_message_at: string
  is_muted?: boolean
}

export type ChatListResponse = {
  items: ChatItem[]
  total: number
}

/* ===================== MESSAGES ===================== */

export type MessageAttachment = {
  file_id: string
  url: string
  filename: string | null
  mime_type: string | null
  size: number | null
  duration: number | null
}

export type MessageReference = { type: "lesson"; id: string }

export type MessageContext = {
  reference: MessageReference | null
  reply_to_message_id: string | null
} | null

export type ChatMessage = {
  id: string
  chat_id: string
  sender_id: string | null
  sender: {
    user_id: string
    full_name: string
    avatar: string
  } | null
  type: "text" | "image" | string
  text: string
  attachments: MessageAttachment[]
  created_at: string
  context?: MessageContext
}

export type MessageListResponse = {
  messages: ChatMessage[]
  total: number
}

/* ===================== API ===================== */

export const listChatsApi = (
  chat_type: "direct" | string,
  limit = 20,
  offset = 0
) => {
  return http.get<ChatListResponse>(
    "/api/chat",
    {
      params: {
        chat_type,
        limit,
        offset,
      },
    }
  )
}


/** `lessonId` uses the backend's built-in filter — only messages referencing that lesson come back. */
export const listChatMessagesApi = (
  chatId: string,
  limit = 50,
  offset = 0,
  lessonId?: string
) => {
  return http.get<MessageListResponse>(
    `/api/chat/${chatId}/messages`,
    {
      params: {
        limit,
        offset,
        lesson_id: lessonId,
      },
    }
  )
}

export type GetOrCreateChatPayload = {
  chat_type: "direct" | "course_group" | "course_private"
  peer_id?: string
  course_id?: string
  student_id?: string
}

export type GetOrCreateChatResponse = {
  chat_id: string
  chat_type: "direct" | "course_group" | "course_private"
}

export const getOrCreateChatApi = (
  payload: GetOrCreateChatPayload
) => {
  return http.post<GetOrCreateChatResponse>(
    "/api/chat/get-or-create",
    payload
  )
}

export type UploadImageResponse = {
  id: string
  url: string
  kind: "image"
}

export type UploadFileResponse = {
  id: string
  file_id: string
  url: string
  kind: "file"
  filename: string
  size: number
}

export const uploadChatImageApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return http.post<UploadImageResponse>(
    "/api/chat/media/upload/image",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  )
}

export const uploadChatFileApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return http.post<UploadFileResponse>(
    "/api/chat/media/upload/file",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  )
}

export const listPrivateChatPeersApi = (
  courseId: string
) => {
  return http.get<ChatParticipant[]>(
    `/api/chat/course/${courseId}/private-peers`
  )
}
